import {
    DelayedSpinner,
    DropdownInput,
    DropdownOption,
    Fieldset,
    IconButton,
    NumberInput,
    Text,
    TextButton,
    TextInput,
} from 'atmosfair-ui';
import React, {memo, useEffect, useRef, useState} from 'react';
import {ActivityHotelState} from '../../../../types-enums/activity-hotel';
import {theme} from '../../../../styles/theme';
import './styles.css'
import '../../styles.css'
import {
    CityResponse,
    CountryResponse,
    HotelCalculationRequest,
    HotelClass,
    HotelResponse,
    HotelRoomCategory
} from '../../../../api/model/activity-hotel';
import {areHotelsEqual, DaysRange, emptyCityResponse, emptyHotelResponse} from './utils';
import {useTranslation} from 'react-i18next';
import {KG_CO2} from "../../../../utils/string/units";
import ToastWithTextOnly from "../../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
// @ts-ignore
import {apiSlice, useCalculateEmissionsMutation,} from '../../../../redux/reducer/api';
import {
    isEmptyCityResponse,
    isEmptyHotelResponse,
    isGeneralCalculatedHotelEmissionsResponse,
    isHotelCalculatedEmissionsResponse
} from '../../../../utils/check/type-guards';
import {toast, TypeOptions} from 'react-toastify';
import {AllActivityTypesArray} from '../../../../types-enums/activity';
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL, CALCULATION_TYPE} from "../../../../api/model/common";
import {v4 as uuidv4} from "uuid";
import {CalculatedEmissionsBackendResponse} from '../../../../api/model/emissions';
import {useAppSelector} from '../../../../redux/reducer/store';
import {
    getMealPlan,
    getRoomCategory,
    getScopeTranslationKey,
    HOTEL_CLASSES,
    MEAL_CLASSES,
    MealPlan,
    ROOM_CLASSES,
    RoomCategory
} from "../../../../screens/activity/activity-form/hotel";
import {ChooseActivityCommonProps, ChooseActivityWithDateCommonProps, createErrorCodeString} from "../../utils";
import AutocompleteQuery, {
    AUTOCOMPLETE_ACTIVITY_TYPE,
    AUTOCOMPLETE_QUERY_TYPE
} from '../../../../molecules/autocomplete-query/AutocompleteQuery';
import cloneDeep from "lodash/cloneDeep";
import DateRangePicker, {
    DATE_RANGE_PICKER_END_DATE,
    DATE_RANGE_PICKER_START_DATE
} from "../../../date-range-picker/DateRangePicker";
import {HOTEL_PROPERTIES, VALIDATE_FORM_STARTING_NUMBER} from "../../../../constants/default-values";
import {hasExactDates} from "../../../../utils/getter/journey";
import {ActivityCommonProperties, DurationProps} from "../../../../types-enums/activity-common-properties";
import {DELAY_TIME_FOR_SPINNER} from "../../../../utils/common";
import {setCommonHotelActivityProps} from "../../../../utils/setter/activities";
import {
    convertHotelClassesFromDropdownFormat,
    convertHotelClassesToDropdownFormat,
    convertMealClassesFromDropdownFormat,
    convertMealClassesToDropdownFormat,
    convertRoomClassesFromDropdownFormat,
    convertRoomClassesToDropdownFormat
} from "../../../../utils/converter/dropdown";
import {getActivityDuration, getDurationInDaysOrNights} from "../../../../utils/getter/common";
import {PATH_SCREEN_STRING} from "../../../../routes/route-props";
import {
    capitalizeFirstLetter,
    generateTotalCo2EAndCo2EPerPersonString,
    NO_DATA_STRING
} from "../../../../utils/string/common";
import {DateTime} from "luxon";
import useFirstRender from "../../../../hooks/use-first-render";
import clsx from "clsx";
import {
    getHotelClassFromHotelResponse,
    getHotelClassNumberValueForCalculationRequest
} from "../../../../utils/getter/hotel";

interface HotelStayProps {
    hotel: HotelResponse | null,
    city: CityResponse | null,
    country: CountryResponse | null,
    stars: HotelClass | null
}

export interface ChooseHotelProps extends ChooseActivityCommonProps, ChooseActivityWithDateCommonProps {
    hotelToEdit: ActivityHotelState | null
    getCalculatedActivity: (activity: ActivityHotelState | null) => void
    journeyDuration: DurationProps,
    activities: AllActivityTypesArray,
    disableDateRangePicker: boolean,
    whichScreenEditFrom: PATH_SCREEN_STRING
}

const INPUT_WIDTH = 30;

interface FormErrorProps {
    guestCountError: boolean,
    numberOfRoomsError: boolean,
    giataIdError: boolean,
    cityError: boolean,
    countryError: boolean,
}

interface FormProps {
    hotelClass: HotelClass | null,
    useExactHotelDates: boolean,
    nightCount: number,
    dateRange: DaysRange,
    roomClass: RoomCategory,
    mealClass: MealPlan | null,
    roomCount: number,
    peopleCount: number,
    giataId: string | null,
}

export const defaultHotelStayDateRange: DaysRange = {
    startDate: DATE_RANGE_PICKER_START_DATE,
    endDate: DATE_RANGE_PICKER_END_DATE
}

const setInitialDateRange = (hotelToEdit: ActivityHotelState | null, minDate: DateTime | undefined) => {
    if (hotelToEdit && hotelToEdit.hotelStayDuration.dates) return hotelToEdit.hotelStayDuration.dates
    if (typeof minDate !== 'undefined') return {
        startDate: minDate,
        endDate: minDate.plus({days: 1})
    }
    return defaultHotelStayDateRange
}

const DEFAULT_ROOM_CLASS = ROOM_CLASSES.find(roomClass => roomClass.value === HotelRoomCategory.DS)!!

const ChooseHotel = memo(function ChooseHotel({
                                                  day,
                                                  hotelToEdit,
                                                  getCalculatedActivity,
                                                  resetForm,
                                                  isEdit,
                                                  journeyDuration,
                                                  minDate,
                                                  maxDate,
                                                  openToDate,
                                                  disableDateRangePicker,
                                                  whichScreenEditFrom,
                                                  maxPersons
                                              }: ChooseHotelProps) {
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [formErrorData, setFormErrorData] = useState<FormErrorProps>({
        guestCountError: false,
        numberOfRoomsError: false,
        giataIdError: false,
        cityError: false,
        countryError: false,
    })
    const [formData, setFormData] = useState<FormProps>({
        hotelClass: null,
        useExactHotelDates: hotelToEdit ? hasExactDates(hotelToEdit?.hotelStayDuration) : false,
        nightCount: getActivityDuration(hotelToEdit) ?? 1,
        dateRange: setInitialDateRange(hotelToEdit, minDate),
        roomClass: DEFAULT_ROOM_CLASS,
        mealClass: null,
        roomCount: 1,
        peopleCount: 1,
        giataId: null
    })
    const [hotelStay, setHotelStay] = useState<HotelStayProps | null>(null)
    const {userData} = useAppSelector(state => state.user)
    const [calculationRequest, setCalculationRequest] = useState<HotelCalculationRequest | null>(null)
    const hotelForRequestFormat = useRef<HotelCalculationRequest[]>([]);
    const [disableCalculateButton, setDisableCalculateButton] = useState(false);
    const {
        guestCountError,
        numberOfRoomsError,
        giataIdError,
        cityError,
        countryError,
    } = formErrorData
    const {
        hotelClass,
        useExactHotelDates,
        nightCount,
        dateRange,
        roomClass,
        mealClass,
        roomCount,
        peopleCount,
        giataId
    } = formData
    const {t, i18n} = useTranslation();
    const {resolvedLanguage} = i18n
    const calculatedActivity = useRef<ActivityHotelState | null>(null)
    const hotelClassDropDownOptions = useRef(convertHotelClassesToDropdownFormat(HOTEL_CLASSES))
    const [calculateEmissions, {
        data: resultCalculateEmissions,
        error: errorCalculateEmissions,
        isLoading: isCalculateEmissions,
    }] = useCalculateEmissionsMutation()
    const [getHotelByGiataId, {
        isLoading: isLoadingGetHotelByGiataId
    }] = apiSlice.endpoints?.getHotelByGiataId.useLazyQuery()
    const isFirstRender = useFirstRender()

    useEffect(() => {
        // only set the formDate with hotelToEdit on first render, otherwise, if user edits data and recalculates,
        // the newly entered data gets replaced with original hotelToEditData
        if (isEdit && hotelToEdit && isFirstRender) {
            const {
                hotel,
                city,
                country,
                hotelClass,
                guestCount,
                numberOfRooms,
                roomCategory,
                mealPlan,
            } = hotelToEdit
            const stars = hotelClass
            setHotelStay({
                hotel: hotel,
                city: city,
                country: country,
                stars: hotelClass
            })
            let room = getRoomCategory(roomCategory)
            if (typeof room === 'undefined') room = null
            let meal = getMealPlan(mealPlan)
            if (typeof meal === 'undefined') meal = null
            setFormData({
                ...formData,
                hotelClass: stars,
                peopleCount: guestCount,
                roomCount: numberOfRooms,
                roomClass: room ?? DEFAULT_ROOM_CLASS,
                mealClass: meal,
            })
        }
    }, [hotelToEdit, isEdit, day])

    useEffect(() => {
        const stars = hotelStay?.stars
        if (stars) {
            if (stars !== HotelClass.zero && stars !== hotelClass) {
                if (hotelStay) setHotelStay({
                    ...hotelStay,
                    hotel: emptyHotelResponse
                })
                _setFormData("giataId", '')
            }
        }
    }, [hotelClass])

    useEffect(() => {
        _fillCalculationRequest()
        if (disableCalculateButton) setDisableCalculateButton(false)
        if (resultCalculateEmissions) {
            calculatedActivity.current = null
            getCalculatedActivity(null)
        }
    }, [hotelStay, hotelClass, roomClass, roomCount, nightCount, peopleCount, mealClass, dateRange, giataId])

    useEffect(() => {
        if (isGeneralCalculatedHotelEmissionsResponse(errorCalculateEmissions)) {
            const {data: {hotelActivities}} = errorCalculateEmissions
            let informativeToastShowed = false;

            const errorStrings: string[] = []
            hotelActivities?.forEach(hotel => {
                if (hotel.errorCodes) hotel.errorCodes.forEach(error => {
                    const {code} = error
                    switch (code) {
                        case 701:
                            errorStrings.push(t(createErrorCodeString(code)));
                            break;
                        case 702:
                            errorStrings.push(t(createErrorCodeString(code)));
                            break;
                        case 703:
                            errorStrings.push(t(createErrorCodeString(code)));
                            break;
                        case 704:
                            errorStrings.push(t(createErrorCodeString(code)));
                            break;
                        case 817:
                            errorStrings.push(t(createErrorCodeString(code)));
                            break;
                        default:
                            errorStrings.push(error.message)
                    }
                })
            })

            if (errorStrings.length > 0) {
                _renderToast(errorStrings.join("\n \n"), "error")
                informativeToastShowed = true
            }

            if (!informativeToastShowed) toast(<ToastWithTextOnly
                    message={"TOASTS.ERRORS.GENERAL"}/>,
                {progress: undefined, type: "error"}
            )
            const emissions = hotelActivities ?? null
            const completeCalculatedActivity: ActivityHotelState = {
                common: {
                    ..._setCommonActivityProps(),
                    totalEmissions: emissions,
                    originalTotalEmissions: cloneDeep(emissions),
                },
                ..._setHotelProps(),
            }
            calculatedActivity.current = completeCalculatedActivity
            getCalculatedActivity(completeCalculatedActivity)
        }
    }, [errorCalculateEmissions])

    useEffect(() => {
        if (resultCalculateEmissions) {
            setDisableCalculateButton(true)
            const {hotelActivities, emissionKgCO2eTotal} = resultCalculateEmissions
            if (typeof hotelActivities !== 'undefined') {
                const calculatedHotel = hotelActivities[0]
                if (isHotelCalculatedEmissionsResponse(calculatedHotel)) {
                    let translationKey = getScopeTranslationKey(calculatedHotel.scope)
                    let scopeOfCalculation
                    if (translationKey) {
                        scopeOfCalculation = t(translationKey)
                        if (resolvedLanguage === "de") {
                            scopeOfCalculation = capitalizeFirstLetter(scopeOfCalculation)
                        } else {
                            scopeOfCalculation = scopeOfCalculation.toLowerCase()
                        }
                    } else {
                        scopeOfCalculation = ''
                    }
                    if (emissionKgCO2eTotal !== null) _renderToast(t("GENERAL.HOTEL_EMISSIONS_CALCULATED_WITH_SCOPE", {scope: scopeOfCalculation}), "success")
                    const completeCalculatedActivity: ActivityHotelState = {
                        common: {
                            ..._setCommonActivityProps(),
                            totalEmissions: hotelActivities,
                            originalTotalEmissions: cloneDeep(hotelActivities)
                        },
                        ..._setHotelProps()
                    }

                    calculatedActivity.current = completeCalculatedActivity
                    getCalculatedActivity(completeCalculatedActivity)
                }
            }
        }
    }, [resultCalculateEmissions])

    useEffect(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER) _evaluateForm()
    }, [validateForm])

    const _setFormData = <K extends keyof FormProps>(
        keyOrUpdates: K | Partial<FormProps>,
        payload?: FormProps[K]
    ) => {
        setFormData({
            ...formData,
            ...(typeof keyOrUpdates === "string" ? {[keyOrUpdates]: payload} : keyOrUpdates)
        })
    }

    const _setFormErrorData = <K extends keyof FormErrorProps>(key: K, payload: FormErrorProps[K]) => setFormErrorData({
        ...formErrorData,
        [key]: payload
    })

    const _setHotelProps = (): Omit<ActivityHotelState, "common"> => {
        const hotelStayDuration = _setHotelStayDuration()

        return {
            hotel: hotelStay !== null ? hotelStay?.hotel : null,
            city: hotelStay !== null ? hotelStay?.city : null,
            country: hotelStay !== null ? hotelStay?.country : null,
            hotelClass: hotelClass,
            roomCategory: roomClass ? roomClass.value : null,
            hotelStayDuration: cloneDeep(hotelStayDuration),
            originalHotelStayDuration: cloneDeep(hotelStayDuration),
            guestCount: peopleCount,
            numberOfRooms: roomCount,
            mealPlan: mealClass ? mealClass.value : null,
            informationFromLoadedBundle: hotelToEdit ? hotelToEdit.informationFromLoadedBundle : null,
            backendId: hotelToEdit?.backendId ?? null
        }
    }

    const _validateGiataId = () => giataId !== null && giataId.length > 0

    const _setCommonActivityProps = (): ActivityCommonProperties['common'] => {
        const id = hotelToEdit?.common.id ?? uuidv4()
        return {
            ...setCommonHotelActivityProps(),
            day,
            editId: null,
            hideLoadBundleInformation: hotelToEdit ? true : false,
            groupId: hotelToEdit?.common.groupId ?? null,
            id,
            semanticId: id,
            originalRequest: cloneDeep(hotelForRequestFormat.current),
            request: hotelForRequestFormat.current,
        }
    }

    const _setHotelStayDuration = (): DurationProps => {
        const {startDate, endDate} = dateRange

        return {
            dates: useExactHotelDates ? {
                startDate: startDate,
                endDate: endDate,
            } : null,
            daysOrNights: useExactHotelDates ? null : nightCount
        }
    }

    const _setCommonHotelRequestProps = (index: number) => ({
        index,
        type: CALCULATION_TYPE.hotel,
        hotelClass: hotelClass ? getHotelClassNumberValueForCalculationRequest(hotelClass) : null,
        ...(roomClass && {roomCategory: roomClass.value}), // cannot set to null here because hotel calculator expects this entry not set if it is null
        numberOfRooms: roomCount,
        numberOfNights: getDurationInDaysOrNights(_setHotelStayDuration(), true)!!,
        numberOfPeople: peopleCount,
        mealPlan: mealClass ? mealClass.value : null
    })

    const _fillCalculationRequest = () => {
        let request: HotelCalculationRequest | null = null
        setCalculationRequest(null);
        const common = _setCommonHotelRequestProps(0)
        if (
            hotelStay !== null &&
            hotelStay.city !== null &&
            !isEmptyCityResponse(hotelStay.city as CityResponse) &&
            hotelStay.country !== null
        ) {
            const hotel = (hotelStay.hotel && !isEmptyHotelResponse(hotelStay.hotel as HotelResponse)) ? hotelStay.hotel : null
            const city = hotelStay.city
            request = {
                hotelId: hotel ? hotel.hotelId : null,
                hotelName: hotel ? hotel.name : null,
                city: city.name,
                region: city.region ?? null,
                country: hotelStay.country.alpha3,
                ...common
            }
        }
        setCalculationRequest(request)
    }

    const _evaluateForm = () => {
        const {
            minNumberGuests,
            minNumberRooms,
            maxNumberRooms
        } = HOTEL_PROPERTIES
        let {maxNumberGuests} = HOTEL_PROPERTIES
        if (maxPersons !== null) maxNumberGuests = maxPersons
        const numberOfPeopleError = !(peopleCount >= minNumberGuests && peopleCount <= maxNumberGuests)
        const numberOfRoomsError = !(roomCount >= minNumberRooms && roomCount <= maxNumberRooms)
        const cityError = (hotelStay?.city == null || isEmptyCityResponse(hotelStay.city))
        const countryError = (hotelStay?.country == null)
        const nightsCountError = !(nightCount >= HOTEL_PROPERTIES.minNumberNights && nightCount <= _getMaxNumberOfNights())

        setFormErrorData({
            ...formErrorData,
            guestCountError: guestCountError,
            numberOfRoomsError,
            cityError,
            countryError,
        })

        if (!(
            numberOfPeopleError ||
            numberOfRoomsError ||
            cityError ||
            countryError ||
            nightsCountError
        )) _calculateEmissions();
    }

    const _calculateEmissions = async () => {
        try {
            if (calculationRequest !== null) {
                hotelForRequestFormat.current = [cloneDeep(calculationRequest)]
                calculateEmissions({hotelActivities: hotelForRequestFormat.current})
            }
        } catch (error) {
            console.error(`Something went wrong when calculating hotel emissions: ${error}`);
        }
    }

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={t(message)}/>,
        {progress: undefined, type}
    )

    // The result of the api call is handled here and not in a useEffect hook because if the hotel is not found twice in
    // a row and is null then the useEffect would not be triggered
    const _getHotelsByGiataId = async () => {
        if (!_validateGiataId()) {
            _setFormErrorData("giataIdError", true)
        } else {
            _setFormErrorData("giataIdError", false)
            const result = await getHotelByGiataId(giataId!!)
            const hotelData = result?.data
            if (hotelData) {
                let toastMessage = 'TOASTS.SUCCESS.HOTEL_FOUND_VIA_GIATA_ID'
                if (hotelStay !== null) toastMessage = `${t(toastMessage)} ${t('TOASTS.SUCCESS.HOTEL_FOUND_VIA_GIATA_ID_REPLACED_PREVIOUS')}`
                toast(
                    <ToastWithTextOnly
                        message={toastMessage}/>,
                    {progress: undefined, type: "success"}
                )
                const newHotelClass = getHotelClassFromHotelResponse(hotelData.stars);
                const copyResultHotelByGiataId = cloneDeep(hotelData)
                copyResultHotelByGiataId.hotelId = giataId
                _setHotel(copyResultHotelByGiataId);
                _setFormData("hotelClass", newHotelClass)
            } else {
                if (_validateGiataId()) {
                    toast(
                        <ToastWithTextOnly
                            message={"TOASTS.ERROR.HOTEL_NOT_FOUND"}/>,
                        {progress: undefined, type: "error"}
                    )
                    if (hotelStay) setHotelStay({
                        ...hotelStay,
                        hotel: emptyHotelResponse
                    })
                    _setFormData("giataId", '')
                }
            }
        }
    }

    const _renderRoomFields = () => {
        let {minNumberRooms, maxNumberRooms} = HOTEL_PROPERTIES
        if (maxPersons !== null) maxNumberRooms = maxPersons
        return <Fieldset
            legend={"SCREENS.HOTEL.ROOM_CATEGORY"}
            showLegend={false}
            fieldsetClasses={clsx('choose-hotel-options-wrapper', 'form-row')}>
            <DropdownInput
                label={"SCREENS.HOTEL.ROOM_CATEGORY"}
                placeholder={"GENERAL.OPTION_OPTIONAL"}
                options={convertRoomClassesToDropdownFormat(ROOM_CLASSES)}
                setSelection={value => _setFormData("roomClass", convertRoomClassesFromDropdownFormat(value)!!)}
                defaultValue={roomClass?.id || null}/>
            <NumberInput
                min={minNumberRooms}
                max={maxNumberRooms}
                error={numberOfRoomsError}
                aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                    min: minNumberRooms,
                    max: maxNumberRooms,
                    words: t("ERRORS.HOTEL.MIN_MAX_ROOMS_EXCEEDED")
                })}
                injectMinimum={true}
                injectMaximum={true}
                required={true}
                label={"SCREENS.HOTEL.ROOMS"}
                onBlur={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormData("roomCount", valueAsNumber)
                }}
                onChange={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormErrorData("numberOfRoomsError", false)
                    _setFormData("roomCount", valueAsNumber)
                }}
                containerStyle={{width: `${INPUT_WIDTH}px`}}
                value={roomCount}/>
        </Fieldset>
    }

    const _renderMealField = () => {
        let {minNumberGuests, maxNumberGuests} = HOTEL_PROPERTIES
        if (maxPersons !== null) maxNumberGuests = maxPersons
        return <Fieldset
            legend={"LABELS.HOTEL.MEAL_PLAN"}
            showLegend={false}
            fieldsetClasses={clsx('choose-hotel-options-wrapper', 'form-row')}>
            <DropdownInput
                label={"SCREENS.HOTEL.MEAL_PLAN"}
                placeholder={"GENERAL.OPTION_OPTIONAL"}
                options={convertMealClassesToDropdownFormat(MEAL_CLASSES)}
                setSelection={value => _setFormData("mealClass", convertMealClassesFromDropdownFormat(value))}
                showDeleteIcon={true}
                defaultValue={mealClass?.id || null}/>
            <NumberInput
                min={minNumberGuests}
                max={maxNumberGuests}
                error={guestCountError}
                aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                    min: minNumberGuests,
                    max: maxNumberGuests,
                    words: t("ERRORS.COMMON.MIN_MAX_PEOPLE_EXCEEDED")
                })}
                injectMinimum={true}
                injectMaximum={true}
                required={true}
                label={"SCREENS.HOTEL.PEOPLE"}
                onBlur={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormData("peopleCount", valueAsNumber)
                }}
                onChange={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormErrorData("guestCountError", false)
                    _setFormData("peopleCount", valueAsNumber)
                }}
                containerStyle={{width: `${INPUT_WIDTH}px`}}
                value={peopleCount}/>
        </Fieldset>
    }

    const _renderResultString = (
        status: CalculatedEmissionsBackendResponse["status"],
        emissionKgCO2eTotal: CalculatedEmissionsBackendResponse["emissionKgCO2eTotal"]
    ) => <Text variant={"headlineMedium"}>
        {
            status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed ?
                generateTotalCo2EAndCo2EPerPersonString(emissionKgCO2eTotal!, hotelForRequestFormat.current[0].numberOfPeople, resolvedLanguage, i18n) :
                `${NO_DATA_STRING} ${KG_CO2}`
        }
    </Text>

    const _renderResult = () => {
        if (resultCalculateEmissions && calculatedActivity.current !== null) {
            const {status, emissionKgCO2eTotal} = resultCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        if (isGeneralCalculatedHotelEmissionsResponse(errorCalculateEmissions)) {
            const {data: {status, emissionKgCO2eTotal}} = errorCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        return null
    }

    const _setEqualPlacesError = (errorValue: boolean) => {
        _setFormErrorData("cityError", errorValue);
        _setFormErrorData("countryError", errorValue)
    }

    const _setHotel = (hotel: HotelResponse | null) => {
        if (hotel) {
            if (!(hotelStay?.hotel && areHotelsEqual(hotel, hotelStay.hotel))) {
                const hotelClass = getHotelClassFromHotelResponse(hotel.stars)
                setHotelStay({
                    hotel,
                    city: {
                        id: 1,
                        name: hotel.city,
                        region: hotel.region,
                        alpha2: hotel.alpha2,
                        alpha3: hotel.alpha3,
                        countryName: hotel.countryName
                    },
                    country: {
                        id: 1,
                        alpha2: hotel.alpha2,
                        alpha3: hotel.alpha3,
                        name: hotel.countryName
                    },
                    stars: hotelClass
                })
                _setFormData("giataId", hotel.hotelId ? hotel.hotelId : '')
                _setFormData("hotelClass", hotelClass)
            }
            _setEqualPlacesError(false)
        } else {
            // empty hotel fields 
            if (hotelStay) setHotelStay({
                ...hotelStay,
                hotel: emptyHotelResponse
            })
            _setFormData("giataId", '')
        }
    }

    const _setCity = (city: CityResponse | null) => {
        if (city) {
            // do not empty other fields if city is the same city of the already filled-in data
            if (
                (hotelStay?.hotel && hotelStay.hotel.city === city?.name) &&
                (hotelStay?.city && hotelStay.city.region === city.region) &&
                (hotelStay?.country && hotelStay.country.alpha2 === city.alpha2)
            ) {
                setHotelStay({
                    ...hotelStay,
                    city
                })
            } else {
                setHotelStay({
                    hotel: emptyHotelResponse,
                    city,
                    country: {
                        id: 1,
                        alpha2: city.alpha2,
                        alpha3: city.alpha3,
                        name: city.countryName
                    },
                    stars: hotelStay ? hotelStay.stars : null
                })
                _setFormData("giataId", '')
            }
            _setEqualPlacesError(false)
        } else {
            // empty hotel and city fields
            if (hotelStay) setHotelStay({
                ...hotelStay,
                hotel: emptyHotelResponse,
                city: emptyCityResponse
            })
            _setFormData("giataId", '')
        }
    }

    const _setCountry = (country: CountryResponse | null) => {
        if ((hotelStay?.hotel && hotelStay.hotel.alpha2 === country?.alpha2) || (hotelStay?.city && hotelStay.city.alpha2 === country?.alpha2)) {
            setHotelStay({
                ...hotelStay,
                country
            })
        } else {
            setHotelStay({
                hotel: emptyHotelResponse,
                city: emptyCityResponse,
                country,
                stars: hotelStay ? hotelStay.stars : null
            })
            _setFormData("giataId", '')
        }
    }

    const _getMaxNumberOfNights = () => {
        if (typeof openToDate !== "undefined" && typeof maxDate !== "undefined") return maxDate.diff(openToDate).as("days")
        if (whichScreenEditFrom === PATH_SCREEN_STRING.tc) return HOTEL_PROPERTIES.maxNumberNights
        if (whichScreenEditFrom === PATH_SCREEN_STRING.journey) {
            const journeyDurationInNights = getDurationInDaysOrNights(journeyDuration, true)
            if (journeyDurationInNights !== null && day !== null) return journeyDurationInNights - day
            return HOTEL_PROPERTIES.maxNumberNights
        }
        return HOTEL_PROPERTIES.maxNumberNights
    }

    const _getDefaultHotelClassOption = (
        dropdownOptions: DropdownOption[],
        selectedValue: HotelClass | undefined | null
    ): number | null => dropdownOptions.filter(option => option.raw.value === selectedValue)[0]?.value

    return <>
        <Text style={{marginBottom: theme.spacer.large}}>
            ACTIVITIES.HOTEL.DESCRIPTION
        </Text>
        <div
            className={"form-row"}
            onKeyDown={event => {
                if (event.key === "Enter") {
                    if (!event.defaultPrevented) {
                        event.preventDefault()
                        setValidateForm(validateForm + 1)
                    }
                }
            }
            }>
            {userData?.userPreferences?.showGiataIdField && <Fieldset
                fieldsetStyles={{alignItems: "center"}}
                legend={"SCREENS.HOTEL.GIATA_ID"}
                showLegend={false}
                fieldsetClasses={clsx('choose-hotel-options-wrapper', 'form-row')}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <TextInput
                        error={giataIdError}
                        aria-errormessage={'ERRORS.HOTEL.GIATA_ID'}
                        required={false}
                        label={"SCREENS.HOTEL.GIATA_ID"}
                        placeholder={"GENERAL.OPTION_OPTIONAL"}
                        onBlur={event => {
                            const {target: {value}} = event
                            _setFormData("giataId", value)
                        }}
                        onChange={event => {
                            const {target: {value}} = event
                            _setFormData("giataId", value)
                        }}
                        value={giataId === null ? undefined : giataId}
                    />
                </div>
                {isLoadingGetHotelByGiataId ?
                    <DelayedSpinner
                        spinnerStyles={{marginLeft: 4, marginTop: 17}}
                        delayMs={DELAY_TIME_FOR_SPINNER}
                        fullscreen={false}
                        size={20}/> :
                    <IconButton
                        containerClasses={'choose-hotel-search-giata-id-icon'}
                        tooltip={"SCREENS.HOTEL.SEARCH"}
                        color={"primary"}
                        size={"medium"}
                        icon={"hotel"}
                        onlyIcon={true}
                        onClick={() => _getHotelsByGiataId()}/>}
            </Fieldset>}
            <Fieldset
                showLegend={true}
                fieldsetClasses={'form-row'}
                required={true}
                legend="SCREENS.HOTEL.HOTEL_INFO">
                <AutocompleteQuery
                    placeholder={"GENERAL.HOTEL"}
                    type={AUTOCOMPLETE_ACTIVITY_TYPE.hotel}
                    label={"GENERAL.HOTEL"}
                    required={false}
                    containerStyle={{flex: 1}}
                    queryType={AUTOCOMPLETE_QUERY_TYPE.hotel}
                    setSelection={hotel => _setHotel(hotel as HotelResponse)}
                    defaultValue={hotelStay?.hotel}
                    secondSearchParam={hotelStay?.city}
                    thirdSearchParam={hotelStay?.country}
                />
                <AutocompleteQuery
                    placeholder={"GENERAL.COUNTRY"}
                    aria-errormessage={"ERRORS.COMMON.COUNTRY"}
                    type={AUTOCOMPLETE_ACTIVITY_TYPE.hotel}
                    label={"GENERAL.COUNTRY"}
                    required={true}
                    containerStyle={{flex: 1}}
                    error={countryError}
                    queryType={AUTOCOMPLETE_QUERY_TYPE.country}
                    setSelection={country => _setCountry(country as CountryResponse)}
                    defaultValue={hotelStay?.country}
                />
                <AutocompleteQuery
                    placeholder={"GENERAL.CITY"}
                    aria-errormessage={"ERRORS.COMMON.CITY"}
                    type={AUTOCOMPLETE_ACTIVITY_TYPE.hotel}
                    label={"GENERAL.CITY"}
                    required={true}
                    containerStyle={{flex: 1}}
                    error={cityError}
                    queryType={AUTOCOMPLETE_QUERY_TYPE.city}
                    setSelection={city => _setCity(city as CityResponse)}
                    defaultValue={hotelStay?.city}
                    secondSearchParam={hotelStay?.country}
                />
                <DropdownInput
                    label={"GENERAL.STARS"}
                    placeholder={"GENERAL.OPTION_OPTIONAL"}
                    options={hotelClassDropDownOptions.current}
                    setSelection={value => _setFormData("hotelClass", convertHotelClassesFromDropdownFormat(value))}
                    containerStyle={{flex: 1}}
                    showDeleteIcon={true}
                    defaultValue={_getDefaultHotelClassOption(hotelClassDropDownOptions.current, hotelClass) || null}/>
            </Fieldset>
            <Fieldset
                showLegend={true}
                fieldsetClasses={'form-row'}
                required={true}
                legend="SCREENS.HOTEL.STAY_INFO">
                {_renderRoomFields()}
                {_renderMealField()}
                <DateRangePicker
                    disableCalendar={disableDateRangePicker}
                    minDate={minDate}
                    maxDate={maxDate}
                    openToDate={openToDate}
                    disableCalendarTooltip={"TOOLTIP.COMMON.CALENDAR_DEACTIVATED_JOURNEY_NO_DATES"}
                    minNumberNightsOrDays={HOTEL_PROPERTIES.minNumberNights}
                    maxNumberNightsOrDays={_getMaxNumberOfNights()}
                    showOnlyCalendarIcon={false}
                    numberInputAriaErrorMessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                        min: HOTEL_PROPERTIES.minNumberNights,
                        max: _getMaxNumberOfNights(),
                        words: t("ERRORS.HOTEL.MIN_MAX_NIGHTS_EXCEEDED")
                    })}
                    isNights={true}
                    containerClasses={clsx('choose-hotel-options-wrapper', 'form-row')}
                    nightsDaysCount={nightCount}
                    setNightsDaysCount={(useDates, value, dateRange) => _setFormData({
                        useExactHotelDates: useDates,
                        nightCount: value,
                        dateRange
                    })}
                    setDateRange={(dateRange: DaysRange, nightsDaysCount: number) => _setFormData({
                        dateRange: dateRange,
                        nightCount: nightsDaysCount
                    })}
                    dateRange={dateRange}
                    useDates={useExactHotelDates}
                    validateForm={validateForm}
                    setUseDates={(useDates, dateRange, nightCount) => {
                        _setFormData({
                            useExactHotelDates: useDates,
                            dateRange,
                            nightCount
                        })
                        if (calculatedActivity.current !== null) {
                            const {startDate, endDate} = dateRange
                            const hotelStayDuration = {
                                dates: useDates ? {
                                    startDate: startDate,
                                    endDate: endDate,
                                } : null,
                                daysOrNights: useDates ? null : nightCount
                            }
                            getCalculatedActivity({
                                ...calculatedActivity.current,
                                hotelStayDuration: cloneDeep(hotelStayDuration),
                                originalHotelStayDuration: cloneDeep(hotelStayDuration)
                            })
                        }
                    }}
                    showMaxHotelDurationExceededToast={true}
                />

            </Fieldset>
            <div className={'calculate-activity-bottom-buttons'}>
                {_renderResult()}
                <div
                    style={{marginTop: theme.spacer.large}}
                    className={'choose-hotel-buttons-wrapper'}>
                    {!isEdit && <TextButton
                        onClick={resetForm}>
                        GENERAL.RESET
                    </TextButton>
                    }
                    <TextButton
                        disabled={disableCalculateButton}
                        onClick={() => setValidateForm(validateForm + 1)}>
                        {isEdit ? "GENERAL.CALCULATE_AGAIN" : "GENERAL.CALCULATE"}
                    </TextButton>
                </div>
                {isCalculateEmissions && <DelayedSpinner fullscreen={true} delayMs={500}/>}
            </div>
        </div>
    </>
})

export default ChooseHotel;
